import type { ProjectStorage } from './ProjectStorage';
import type { StorageConfig } from './storageConfig';

export class LocalProjectStorage<TLevel> implements ProjectStorage<TLevel> {
  private config: StorageConfig;

  constructor(config: StorageConfig) {
    this.config = config;
  }

  private lineupKey(): string {
    const { workspaceRoot, lineupFile } = this.config;
    return `${workspaceRoot}/${lineupFile}`;
  }

  private levelKey(id: string): string {
    const { workspaceRoot, levelsDir } = this.config;
    return `${workspaceRoot}/${levelsDir}/${id}.json`;
  }

  private levelKeyPrefix(): string {
    const { workspaceRoot, levelsDir } = this.config;
    return `${workspaceRoot}/${levelsDir}/`;
  }

  private imagesKey(): string {
    return `${this.config.workspaceRoot}/${this.config.imagesDir}/__index`;
  }

  async loadLineup(): Promise<string[]> {
    const raw = localStorage.getItem(this.lineupKey());
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as { levels: string[] };
      return Array.isArray(parsed.levels) ? parsed.levels : [];
    } catch {
      return [];
    }
  }

  async saveLineup(levelIds: string[]): Promise<void> {
    localStorage.setItem(this.lineupKey(), JSON.stringify({ levels: levelIds }));
  }

  async listLevels(): Promise<string[]> {
    const prefix = this.levelKeyPrefix();
    const names: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix) && key.endsWith('.json')) {
        names.push(key.slice(prefix.length, -'.json'.length));
      }
    }
    return names;
  }

  async loadLevel(id: string): Promise<TLevel> {
    const raw = localStorage.getItem(this.levelKey(id));
    if (!raw) throw new Error(`Level not found: ${id}`);
    return JSON.parse(raw) as TLevel;
  }

  async saveLevel(id: string, level: TLevel): Promise<void> {
    localStorage.setItem(this.levelKey(id), JSON.stringify(level));
  }

  async deleteLevel(id: string): Promise<void> {
    localStorage.removeItem(this.levelKey(id));
    const lineup = await this.loadLineup();
    await this.saveLineup(lineup.filter(lid => lid !== id));
  }

  async listImages(): Promise<string[]> {
    const raw = localStorage.getItem(this.imagesKey());
    if (!raw) return Promise.resolve([]);
    try {
      return Promise.resolve(JSON.parse(raw) as string[]);
    } catch {
      return Promise.resolve([]);
    }
  }

  getImageUrl(path: string): string {
    return `/${path}`;
  }
}
